import { useState, useEffect, useCallback } from "react";

const phases = [
  {
    id: 1,
    title: "Foundation Setup",
    subtitle: "Your Development Environment",
    duration: "Week 1",
    color: "#0F766E",
    accent: "#14B8A6",
    icon: "🖥️",
    concepts: [
      {
        title: "What You Need to Know First",
        content: `Before touching any robot code, let's understand the architecture. Your Go2 Edu is special — it runs on **CycloneDDS** (Data Distribution Service), which is the same middleware that ROS2 uses under the hood. This means you have TWO paths to control your robot:

**Path A: Unitree SDK2 (Python)** — Direct control without ROS2. Simpler, faster to start.
**Path B: ROS2** — Full robotics framework with visualization, SLAM, navigation, etc.

**My recommendation:** Start with Path A (SDK2 Python) to get moving fast, then layer in ROS2 when you need advanced features like mapping or autonomous navigation.`
      },
      {
        title: "Step 1: Install Ubuntu 22.04",
        content: `ROS2 Humble (the version you'll use) requires Ubuntu 22.04 LTS. If you're on Windows, you have options:

**Option A (Recommended): Dual Boot Ubuntu 22.04**
Download Ubuntu 22.04 LTS from ubuntu.com and install alongside Windows.

**Option B: WSL2 on Windows**
Works for SDK development but NOT ideal for ROS2 visualization tools.

**Option C: Virtual Machine**
Use VirtualBox/VMware — works but slower for real-time robot control.

⚠️ Make sure you have: Python 3.8+, pip3, git, build-essential installed.`
      },
      {
        title: "Step 2: Install Unitree SDK2 Python",
        content: `This is your direct line to the robot. Run these commands in order:

\`\`\`bash
# 1. Install CycloneDDS (the communication layer)
cd ~
git clone https://github.com/eclipse-cyclonedds/cyclonedds -b releases/0.10.x
cd cyclonedds && mkdir build install && cd build
cmake .. -DCMAKE_INSTALL_PREFIX=../install
cmake --build . --target install

# 2. Install the Python SDK
cd ~
git clone https://github.com/unitreerobotics/unitree_sdk2_python.git
cd unitree_sdk2_python
export CYCLONEDDS_HOME=~/cyclonedds/install
pip3 install -e .
\`\`\`

**What just happened?** CycloneDDS is how your computer talks to the robot using a publish/subscribe pattern (like a chat room where your code and the robot exchange messages on "topics").`
      },
      {
        title: "Step 3: Connect to Your Go2 via Ethernet",
        content: `The Edu version supports direct Ethernet connection for the best performance:

1. Connect an Ethernet cable from your computer to the Go2
2. Open network settings, find the Ethernet interface (e.g., "enp3s0")
3. Set IPv4 to Manual:
   — Address: **192.168.123.99**
   — Netmask: **255.255.255.0**
4. Apply and wait for reconnection

**Verify connection:**
\`\`\`bash
ping 192.168.123.161  # Robot's default IP
\`\`\`

If you get responses, you're connected! 🎉

**💡 Teaching moment — Why Ethernet?** 
Wi-Fi adds latency and packet loss. For real-time robot control (especially low-level motor commands), Ethernet gives you reliable, sub-millisecond communication.`
      }
    ]
  },
  {
    id: 2,
    title: "First Contact",
    subtitle: "Talk to Your Robot",
    duration: "Week 1-2",
    color: "#7C3AED",
    accent: "#A78BFA",
    icon: "🐕",
    concepts: [
      {
        title: "Understanding DDS Topics (Core Concept)",
        content: `DDS (and ROS2) use a **publish/subscribe** pattern. Think of it like radio channels:

**Publisher:** Broadcasts data on a "topic" (channel name)
**Subscriber:** Listens to a topic and receives data

Your Go2 constantly publishes data on topics like:
— \`rt/sportmodestate\` → Robot's current position, velocity, foot positions
— \`rt/lf/lowstate\` → Joint angles, IMU data, battery level

And it listens on topics like:
— \`rt/api/sport/request\` → High-level movement commands
— \`rt/lowcmd\` → Direct motor commands (advanced!)

**Key insight:** You never "connect" to the robot directly. You just publish/subscribe to the same topics, and DDS handles discovery automatically on the network.`
      },
      {
        title: "Step 4: Read Robot State (Your First Script)",
        content: `Let's write your first script to read the robot's state:

\`\`\`python
# my_first_go2.py
import time
from unitree_sdk2py.core.channel import ChannelSubscriber, ChannelFactoryInitialize
from unitree_sdk2py.idl.default import unitree_go2_msg_dds__SportModeState_
import sys

def state_callback(msg: unitree_go2_msg_dds__SportModeState_):
    print(f"Position: x={msg.position[0]:.2f}, y={msg.position[1]:.2f}")
    print(f"Velocity: vx={msg.velocity[0]:.2f}, vy={msg.velocity[1]:.2f}")
    print(f"Battery: {msg.bms_state.soc}%")
    print("---")

if __name__ == "__main__":
    if len(sys.argv) < 2:
        print("Usage: python3 my_first_go2.py <network_interface>")
        sys.exit(1)
    
    ChannelFactoryInitialize(0, sys.argv[1])
    sub = ChannelSubscriber("rt/sportmodestate",
                            unitree_go2_msg_dds__SportModeState_)
    sub.Init(state_callback, 10)
    
    while True:
        time.sleep(1)
\`\`\`

Run it: \`python3 my_first_go2.py enp3s0\`
(Replace enp3s0 with your network interface name from \`ifconfig\`)

You should see real-time robot data streaming! 🎉`
      },
      {
        title: "Step 5: Make It Move! (High-Level Control)",
        content: `Now the exciting part — commanding movement using the SportClient:

\`\`\`python
# make_it_walk.py
import time
import sys
from unitree_sdk2py.core.channel import ChannelFactoryInitialize
from unitree_sdk2py.go2.sport.sport_client import SportClient

if __name__ == "__main__":
    ChannelFactoryInitialize(0, sys.argv[1])
    
    sport = SportClient()
    sport.SetTimeout(5.0)
    sport.Init()
    time.sleep(1)  # Wait for connection
    
    # Stand up
    print("Standing up...")
    sport.StandUp()
    time.sleep(2)
    
    # Walk forward slowly (vx=0.3 m/s, vy=0, vyaw=0)
    print("Walking forward...")
    sport.Move(0.3, 0.0, 0.0)
    time.sleep(3)
    
    # Stop
    print("Stopping...")
    sport.StopMove()
    time.sleep(1)
    
    # Sit down
    print("Sitting...")
    sport.StandDown()
\`\`\`

**⚠️ SAFETY:** Always test in an open area. Keep the remote controller handy to emergency stop (L2+B).

**SportClient has tons of methods:**
— \`Move(vx, vy, vyaw)\` — Walk in any direction
— \`StandUp()\` / \`StandDown()\` — Stand/sit
— \`Hello()\` — Wave! 
— \`Dance1()\`, \`Dance2()\` — Dance routines
— \`BackFlip()\`, \`FrontFlip()\` — Acrobatics (be careful!)
— \`BalanceStand()\` — Balanced standing pose
— \`RecoveryStand()\` — Get up after falling`
      },
      {
        title: "Step 6: Access the Camera",
        content: `Your Go2 has a front camera. Let's stream it:

\`\`\`python
# camera_stream.py
import sys
import cv2
import numpy as np
from unitree_sdk2py.core.channel import ChannelFactoryInitialize
from unitree_sdk2py.go2.video.video_client import VideoClient

if __name__ == "__main__":
    ChannelFactoryInitialize(0, sys.argv[1])
    
    client = VideoClient()
    client.SetTimeout(3.0)
    client.Init()
    
    while True:
        code, data = client.GetImageSample()
        if code == 0:
            img = np.frombuffer(bytes(data), dtype=np.uint8)
            frame = cv2.imdecode(img, cv2.IMREAD_COLOR)
            cv2.imshow("Go2 Camera", frame)
            if cv2.waitKey(1) & 0xFF == 27:  # ESC to quit
                break
    
    cv2.destroyAllWindows()
\`\`\`

You'll need OpenCV: \`pip3 install opencv-python\``
      }
    ]
  },
  {
    id: 3,
    title: "Learn ROS2 Basics",
    subtitle: "The Robotics Framework",
    duration: "Week 2-3",
    color: "#B45309",
    accent: "#F59E0B",
    icon: "🧠",
    concepts: [
      {
        title: "Why ROS2? (And What Is It Really?)",
        content: `**ROS2 is NOT an operating system.** It's a framework (set of tools + libraries) that helps robots:

1. **Communicate** — Topics, Services, Actions (pub/sub + request/response)
2. **Visualize** — RViz2 (see your robot in 3D, view sensor data)
3. **Navigate** — Nav2 (autonomous path planning)
4. **Map** — SLAM toolbox (create maps from LiDAR)
5. **Simulate** — Gazebo (test before running on real hardware)

**Why you need it:** SDK2 alone lets you move the robot. ROS2 lets you build intelligent behaviors — map a room, avoid obstacles autonomously, plan paths, and visualize everything.

**Key ROS2 concepts you'll use:**
— **Node**: A single process that does one thing (e.g., "camera_node", "movement_node")
— **Topic**: Named channel for streaming data (like DDS topics you already used!)
— **Service**: Request/response pattern (ask the robot to do something, get confirmation)
— **Launch file**: Script that starts multiple nodes at once`
      },
      {
        title: "Step 7: Install ROS2 Humble",
        content: `On Ubuntu 22.04:

\`\`\`bash
# Add ROS2 repository
sudo apt update && sudo apt install software-properties-common
sudo add-apt-repository universe
sudo apt update && sudo apt install curl -y
sudo curl -sSL https://raw.githubusercontent.com/ros/rosdistro/master/ros.key \\
  -o /usr/share/keyrings/ros-archive-keyring.gpg
echo "deb [arch=$(dpkg --print-architecture) \\
  signed-by=/usr/share/keyrings/ros-archive-keyring.gpg] \\
  http://packages.ros.org/ros2/ubuntu $(. /etc/os-release && echo $UBUNTU_CODENAME) main" \\
  | sudo tee /etc/apt/sources.list.d/ros2.list > /dev/null

# Install ROS2 Humble Desktop (includes RViz2, demos)
sudo apt update
sudo apt install ros-humble-desktop -y

# Add to your shell (so it loads every time)
echo "source /opt/ros/humble/setup.bash" >> ~/.bashrc
source ~/.bashrc
\`\`\`

**Verify it works:**
\`\`\`bash
ros2 topic list   # Should show /rosout, /parameter_events
\`\`\``
      },
      {
        title: "Step 8: ROS2 Hands-On Tutorial (30 min)",
        content: `Before connecting to the robot via ROS2, practice the basics:

**Terminal 1 — Run a talker:**
\`\`\`bash
ros2 run demo_nodes_cpp talker
\`\`\`

**Terminal 2 — Run a listener:**
\`\`\`bash
ros2 run demo_nodes_cpp listener
\`\`\`

You'll see messages flowing! Now try these essential commands:

\`\`\`bash
ros2 topic list          # See all active topics
ros2 topic echo /chatter # Listen to a topic
ros2 topic info /chatter # See who publishes/subscribes
ros2 node list           # See all active nodes
ros2 node info /talker   # Details about a node
\`\`\`

**💡 Teaching moment:** This is exactly how your Go2 works! Replace "/chatter" with "/sportmodestate" and the talker with your robot — same concept, real hardware.`
      },
      {
        title: "Step 9: Set Up Unitree ROS2 Package",
        content: `Now connect ROS2 to your Go2:

\`\`\`bash
# Create a workspace
mkdir -p ~/unitree_ros2_ws/src
cd ~/unitree_ros2_ws/src

# Clone the official Unitree ROS2 package
git clone https://github.com/unitreerobotics/unitree_ros2.git

# Build CycloneDDS for ROS2
cd unitree_ros2/cyclonedds_ws
colcon build --packages-select cyclonedds
source install/setup.bash

# Build Unitree packages
cd ~/unitree_ros2_ws
source /opt/ros/humble/setup.bash
colcon build
source install/setup.bash
\`\`\`

**Configure the network interface** in setup.sh, then:
\`\`\`bash
# See your robot's data as ROS2 topics!
ros2 topic list
ros2 topic echo /sportmodestate
\`\`\`

Now you can use ALL of ROS2's tools with your robot data! 🚀`
      }
    ]
  },
  {
    id: 4,
    title: "Build Your Control App",
    subtitle: "The Fun Part!",
    duration: "Week 3-5",
    color: "#DC2626",
    accent: "#F87171",
    icon: "📱",
    concepts: [
      {
        title: "App Architecture Decision",
        content: `Now you know the basics. Time to decide what kind of app you want to build. Here are your options:

**Option 1: Web App (Recommended for beginners)**
— Python backend (Flask/FastAPI) + HTML/JS frontend
— Runs in any browser, accessible from phone/tablet
— Uses SDK2 Python on the backend

**Option 2: Desktop App**
— Python with PyQt/Tkinter
— Direct SDK2 integration
— Good for dashboards with video feeds

**Option 3: Mobile App**
— React Native or Flutter
— Communicates via WebSocket to a Python backend on your PC
— Most polished end result

**Option 4: ROS2 Native**
— RViz2 custom panels
— Best for visualization + autonomous navigation
— Steepest learning curve

**My recommendation: Option 1 (Web App)** — it's the fastest path to a working controller you can use from any device.`
      },
      {
        title: "Step 10: Build a Web Controller (Backend)",
        content: `Here's a FastAPI backend that bridges your web app to the robot:

\`\`\`python
# backend.py
import asyncio
from fastapi import FastAPI, WebSocket
from fastapi.staticfiles import StaticFiles
from unitree_sdk2py.core.channel import ChannelFactoryInitialize
from unitree_sdk2py.go2.sport.sport_client import SportClient
import json, time, threading

app = FastAPI()

# Initialize robot connection
ChannelFactoryInitialize(0, "enp3s0")  # Your interface
sport = SportClient()
sport.SetTimeout(5.0)
sport.Init()
time.sleep(1)

@app.post("/api/stand-up")
async def stand_up():
    sport.StandUp()
    return {"status": "ok", "action": "stand_up"}

@app.post("/api/sit-down")
async def sit_down():
    sport.StandDown()
    return {"status": "ok", "action": "sit_down"}

@app.post("/api/move")
async def move(vx: float = 0, vy: float = 0, vyaw: float = 0):
    sport.Move(vx, vy, vyaw)
    return {"status": "ok", "vx": vx, "vy": vy, "vyaw": vyaw}

@app.post("/api/stop")
async def stop():
    sport.StopMove()
    return {"status": "ok", "action": "stop"}

@app.post("/api/action/{name}")
async def action(name: str):
    actions = {
        "hello": sport.Hello,
        "dance1": sport.Dance1,
        "dance2": sport.Dance2,
        "stretch": sport.Stretch,
        "sit": sport.Sit,
        "balance": sport.BalanceStand,
        "recovery": sport.RecoveryStand,
    }
    if name in actions:
        actions[name]()
        return {"status": "ok", "action": name}
    return {"status": "error", "msg": "Unknown action"}

app.mount("/", StaticFiles(directory="static", html=True))
\`\`\`

Install: \`pip3 install fastapi uvicorn\`
Run: \`uvicorn backend:app --host 0.0.0.0 --port 8000\``
      },
      {
        title: "Step 11: Build the Frontend Controller",
        content: `Create a \`static/index.html\` with a virtual joystick and action buttons. You'll want:

1. **A virtual joystick** — Drag to control vx/vy, with a rotation slider for vyaw
2. **Quick action buttons** — Stand, Sit, Dance, Wave, etc.
3. **Status display** — Battery, position, connection state
4. **Camera feed** — Stream from the Go2's front camera

The frontend sends commands to your FastAPI backend via HTTP POST requests or WebSockets (for real-time joystick streaming).

**Key libraries to consider:**
— \`nipplejs\` — Virtual joystick library for touch/mouse
— WebSocket for real-time bidirectional control
— \`<canvas>\` for camera feed rendering

This is where you get creative! Design the UI that works for YOUR use case — whether it's a simple remote control, a monitoring dashboard, or a full mission planner.`
      },
      {
        title: "Step 12: Add Real-Time Status via WebSocket",
        content: `Upgrade your backend to stream robot state in real-time:

\`\`\`python
# Add to backend.py
from unitree_sdk2py.idl.default import unitree_go2_msg_dds__SportModeState_

robot_state = {}

def state_handler(msg):
    global robot_state
    robot_state = {
        "position": {"x": msg.position[0], "y": msg.position[1]},
        "velocity": {"vx": msg.velocity[0], "vy": msg.velocity[1]},
        "battery": msg.bms_state.soc,
        "mode": msg.mode,
    }

# Set up subscriber in a thread
sub = ChannelSubscriber("rt/sportmodestate",
                        unitree_go2_msg_dds__SportModeState_)
sub.Init(state_handler, 10)

@app.websocket("/ws/state")
async def websocket_state(websocket: WebSocket):
    await websocket.accept()
    while True:
        if robot_state:
            await websocket.send_json(robot_state)
        await asyncio.sleep(0.1)  # 10 Hz updates
\`\`\`

Your frontend connects via WebSocket and gets live robot data at 10Hz — battery, position, velocity, all in real-time!`
      }
    ]
  },
  {
    id: 5,
    title: "Advanced Features",
    subtitle: "Level Up Your App",
    duration: "Week 5+",
    color: "#0369A1",
    accent: "#38BDF8",
    icon: "🚀",
    concepts: [
      {
        title: "SLAM & Autonomous Navigation",
        content: `Once comfortable with basic control, add intelligence:

**SLAM (Simultaneous Localization and Mapping)**
Your Go2 Edu has a LiDAR sensor. Using ROS2's slam_toolbox:
\`\`\`bash
sudo apt install ros-humble-slam-toolbox
ros2 launch slam_toolbox online_async_launch.py
\`\`\`
Drive the robot around manually → it builds a 2D map!

**Nav2 (Autonomous Navigation)**
\`\`\`bash
sudo apt install ros-humble-navigation2 ros-humble-nav2-bringup
\`\`\`
Click a point on the map in RViz2 → the robot plans a path and walks there autonomously, avoiding obstacles.

**Integration with your app:**
Add a map view to your web frontend where you can click to set navigation goals. The backend sends these as Nav2 goals via ROS2 action calls.`
      },
      {
        title: "Computer Vision & Object Detection",
        content: `Use the Go2's camera for intelligent behaviors:

**Object Detection with YOLO:**
\`\`\`python
# Detect objects from Go2's camera feed
from ultralytics import YOLO
model = YOLO("yolov8n.pt")

# In your camera callback:
results = model(frame)
for r in results:
    for box in r.boxes:
        cls = model.names[int(box.cls[0])]
        conf = float(box.conf[0])
        print(f"Detected {cls} ({conf:.0%})")
\`\`\`

**Ideas:**
— Follow a specific person (person tracking)
— Patrol and detect intruders
— Find and navigate to specific objects
— React to hand gestures`
      },
      {
        title: "Multi-Robot Control",
        content: `Since you mentioned multiple Go2 dogs, the SDK supports multi-robot setups:

Each robot has a unique network configuration. In your app, you can manage multiple SportClient instances:

\`\`\`python
# Each robot gets its own client
robots = {}
for robot_id, interface in robot_configs.items():
    ChannelFactoryInitialize(robot_id, interface)
    client = SportClient()
    client.Init()
    robots[robot_id] = client

# Send coordinated commands
for name, client in robots.items():
    client.Move(0.3, 0.0, 0.0)  # All walk forward
\`\`\`

Build a fleet management dashboard showing all dogs' positions, battery levels, and camera feeds on one screen!`
      },
      {
        title: "Voice Control & AI Integration",
        content: `Add voice commands or AI-powered control:

**Voice Control Pipeline:**
1. Microphone → Speech-to-Text (Whisper API)
2. Text → Intent parsing (GPT or local NLP)
3. Intent → Robot command (your mapping)

**Example intents:**
— "Go to the kitchen" → Nav2 goal to saved waypoint
— "Do a dance" → sport.Dance1()
— "Follow me" → Activate follow mode
— "Patrol the house" → Execute waypoint sequence

**LLM-powered control:**
Connect ChatGPT/Claude API to interpret natural language commands and translate them to robot actions. This is a hot research area!`
      }
    ]
  }
];

const resources = [
  { name: "Unitree SDK2 Python (GitHub)", url: "github.com/unitreerobotics/unitree_sdk2_python", tag: "Essential" },
  { name: "Unitree ROS2 Package", url: "github.com/unitreerobotics/unitree_ros2", tag: "Essential" },
  { name: "Unitree Developer Docs", url: "support.unitree.com/home/en/developer", tag: "Essential" },
  { name: "Go2 Community ROS2 SDK", url: "github.com/abizovnuralem/go2_ros2_sdk", tag: "Community" },
  { name: "ROS2 Humble Tutorials", url: "docs.ros.org/en/humble/Tutorials.html", tag: "Learning" },
  { name: "DeepWiki Go2 SDK Docs", url: "deepwiki.com/unitreerobotics/unitree_sdk2_python", tag: "Reference" },
];

const STORAGE_KEY = "go2-roadmap-progress";

function loadProgress() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
}

function saveProgress(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

export default function Go2Roadmap() {
  const saved = loadProgress();
  const [activePhase, setActivePhase] = useState(saved?.activePhase ?? 0);
  const [activeStep, setActiveStep] = useState(saved?.activeStep ?? 0);
  const [completedSteps, setCompletedSteps] = useState(saved?.completedSteps ?? {});
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    saveProgress({ activePhase, activeStep, completedSteps });
  }, [activePhase, activeStep, completedSteps]);

  const resetProgress = useCallback(() => {
    setCompletedSteps({});
    setActivePhase(0);
    setActiveStep(0);
    localStorage.removeItem(STORAGE_KEY);
    setShowResetConfirm(false);
  }, []);

  const toggleComplete = (phaseIdx, stepIdx) => {
    const key = `${phaseIdx}-${stepIdx}`;
    setCompletedSteps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totalSteps = phases.reduce((acc, p) => acc + p.concepts.length, 0);
  const completedCount = Object.values(completedSteps).filter(Boolean).length;
  const progress = Math.round((completedCount / totalSteps) * 100);

  const currentPhase = phases[activePhase];
  const currentStep = currentPhase.concepts[activeStep];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0A0A0F",
      color: "#E2E8F0",
      fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
    }}>
      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => setShowResetConfirm(false)}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#1A1A2E", border: "1px solid #2D3748",
              borderRadius: 12, padding: "28px 32px", maxWidth: 380, width: "90%",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#F1F5F9" }}>
              Reset All Progress?
            </h3>
            <p style={{ margin: "0 0 24px", fontSize: 13, color: "#94A3B8", lineHeight: 1.5 }}>
              This will clear all your completed steps and reset everything to the beginning. This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setShowResetConfirm(false)}
                style={{
                  background: "#2D3748", border: "none", color: "#E2E8F0",
                  padding: "8px 20px", borderRadius: 6, cursor: "pointer",
                  fontSize: 13, fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                onClick={resetProgress}
                style={{
                  background: "#EF4444", border: "none", color: "#fff",
                  padding: "8px 20px", borderRadius: 6, cursor: "pointer",
                  fontSize: 13, fontFamily: "inherit", fontWeight: 600,
                }}
              >
                Reset Progress
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #0A0A0F 0%, #1A1A2E 50%, #0A0A0F 100%)",
        borderBottom: "1px solid #1E293B",
        padding: "24px 32px",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
            <span style={{ fontSize: 32 }}>🤖</span>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                background: "linear-gradient(90deg, #14B8A6, #A78BFA, #F59E0B)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.5px",
              }}>
                Unitree Go2 Edu — Developer Roadmap
              </h1>
              <p style={{ margin: 0, fontSize: 12, color: "#64748B", marginTop: 4 }}>
                From zero to robot app developer — your personalized learning path
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div style={{ marginTop: 16 }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              fontSize: 11, color: "#64748B", marginBottom: 6,
            }}>
              <span>{completedCount} / {totalSteps} steps completed</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span>{progress}%</span>
                {completedCount > 0 && (
                  <button
                    onClick={() => setShowResetConfirm(true)}
                    style={{
                      background: "transparent",
                      border: "1px solid #EF4444",
                      color: "#EF4444",
                      fontSize: 10,
                      padding: "3px 10px",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={e => { e.target.style.background = "#EF4444"; e.target.style.color = "#fff"; }}
                    onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = "#EF4444"; }}
                  >
                    Reset Progress
                  </button>
                )}
              </div>
            </div>
            <div style={{
              height: 6, borderRadius: 3,
              background: "#1E293B",
              overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: `${progress}%`,
                borderRadius: 3,
                background: "linear-gradient(90deg, #14B8A6, #A78BFA)",
                transition: "width 0.5s ease",
              }} />
            </div>
            {completedCount > 0 && (
              <div style={{ fontSize: 10, color: "#14B8A6", marginTop: 6, opacity: 0.7 }}>
                Progress auto-saved to your browser
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px" }}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>

          {/* Left Sidebar — Phase Navigation */}
          <div style={{ width: 280, flexShrink: 0 }}>
            {phases.map((phase, idx) => {
              const phaseSteps = phase.concepts.length;
              const phaseCompleted = phase.concepts.filter((_, sIdx) =>
                completedSteps[`${idx}-${sIdx}`]
              ).length;
              const isActive = idx === activePhase;

              return (
                <button
                  key={phase.id}
                  onClick={() => { setActivePhase(idx); setActiveStep(0); }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "16px",
                    marginBottom: 8,
                    background: isActive
                      ? `linear-gradient(135deg, ${phase.color}22, ${phase.color}11)`
                      : "transparent",
                    border: `1px solid ${isActive ? phase.accent + "44" : "#1E293B"}`,
                    borderRadius: 12,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    color: "#E2E8F0",
                    fontFamily: "inherit",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 22 }}>{phase.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: isActive ? phase.accent : "#CBD5E1",
                      }}>
                        {phase.title}
                      </div>
                      <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                        {phase.subtitle} — {phase.duration}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 10,
                      padding: "2px 8px",
                      borderRadius: 10,
                      background: phaseCompleted === phaseSteps
                        ? "#14532D" : "#1E293B",
                      color: phaseCompleted === phaseSteps
                        ? "#4ADE80" : "#64748B",
                    }}>
                      {phaseCompleted}/{phaseSteps}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Resources */}
            <div style={{
              marginTop: 20,
              padding: 16,
              border: "1px solid #1E293B",
              borderRadius: 12,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: "#94A3B8",
                marginBottom: 10, textTransform: "uppercase", letterSpacing: 1,
              }}>
                Key Resources
              </div>
              {resources.map((r, i) => (
                <div key={i} style={{
                  fontSize: 11,
                  padding: "6px 0",
                  borderBottom: i < resources.length - 1 ? "1px solid #1E293B" : "none",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <span style={{ color: "#CBD5E1" }}>{r.name}</span>
                  <span style={{
                    fontSize: 9, padding: "1px 6px", borderRadius: 6,
                    background: r.tag === "Essential" ? "#7C3AED22" : r.tag === "Community" ? "#0F766E22" : "#B4530922",
                    color: r.tag === "Essential" ? "#A78BFA" : r.tag === "Community" ? "#14B8A6" : "#F59E0B",
                  }}>{r.tag}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Step tabs */}
            <div style={{
              display: "flex", gap: 6, marginBottom: 16,
              overflowX: "auto", paddingBottom: 4,
            }}>
              {currentPhase.concepts.map((step, idx) => {
                const isComplete = completedSteps[`${activePhase}-${idx}`];
                const isStepActive = idx === activeStep;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveStep(idx)}
                    style={{
                      padding: "8px 14px",
                      fontSize: 11,
                      fontWeight: isStepActive ? 600 : 400,
                      borderRadius: 8,
                      border: `1px solid ${isStepActive ? currentPhase.accent + "66" : "#1E293B"}`,
                      background: isStepActive ? currentPhase.color + "22" : "transparent",
                      color: isStepActive ? currentPhase.accent : "#94A3B8",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      fontFamily: "inherit",
                      display: "flex", alignItems: "center", gap: 6,
                    }}
                  >
                    {isComplete && <span style={{ color: "#4ADE80" }}>✓</span>}
                    {step.title.replace(/Step \d+: /, "").substring(0, 24)}
                    {step.title.replace(/Step \d+: /, "").length > 24 ? "…" : ""}
                  </button>
                );
              })}
            </div>

            {/* Step Content */}
            <div style={{
              background: "#111118",
              border: "1px solid #1E293B",
              borderRadius: 16,
              padding: 32,
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "flex-start", marginBottom: 20,
              }}>
                <div>
                  <div style={{
                    fontSize: 10, color: currentPhase.accent,
                    textTransform: "uppercase", letterSpacing: 1.5,
                    marginBottom: 6,
                  }}>
                    Phase {activePhase + 1} — {currentPhase.title}
                  </div>
                  <h2 style={{
                    margin: 0, fontSize: 20, fontWeight: 700,
                    color: "#F1F5F9",
                  }}>
                    {currentStep.title}
                  </h2>
                </div>
                <button
                  onClick={() => toggleComplete(activePhase, activeStep)}
                  style={{
                    padding: "8px 16px",
                    fontSize: 11,
                    borderRadius: 8,
                    border: completedSteps[`${activePhase}-${activeStep}`]
                      ? "1px solid #4ADE8044"
                      : "1px solid #374151",
                    background: completedSteps[`${activePhase}-${activeStep}`]
                      ? "#14532D22" : "transparent",
                    color: completedSteps[`${activePhase}-${activeStep}`]
                      ? "#4ADE80" : "#94A3B8",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontWeight: 600,
                  }}
                >
                  {completedSteps[`${activePhase}-${activeStep}`]
                    ? "✓ Completed" : "Mark Complete"}
                </button>
              </div>

              {/* Render content with basic markdown */}
              <div style={{
                fontSize: 13,
                lineHeight: 1.8,
                color: "#CBD5E1",
              }}>
                {currentStep.content.split("\n").map((line, i) => {
                  // Code blocks
                  if (line.startsWith("```")) {
                    return null; // Handled by block logic below
                  }

                  // Bold headers
                  if (line.startsWith("**") && line.endsWith("**")) {
                    return (
                      <p key={i} style={{
                        fontWeight: 700,
                        color: "#F1F5F9",
                        margin: "16px 0 8px",
                        fontSize: 14,
                      }}>
                        {line.replace(/\*\*/g, "")}
                      </p>
                    );
                  }

                  // Lines with bold sections
                  if (line.includes("**")) {
                    const parts = line.split(/(\*\*[^*]+\*\*)/);
                    return (
                      <p key={i} style={{ margin: "4px 0" }}>
                        {parts.map((part, j) =>
                          part.startsWith("**") ? (
                            <strong key={j} style={{ color: "#F1F5F9" }}>
                              {part.replace(/\*\*/g, "")}
                            </strong>
                          ) : part
                        )}
                      </p>
                    );
                  }

                  // Dash lists
                  if (line.startsWith("—") || line.startsWith("- ")) {
                    return (
                      <div key={i} style={{
                        paddingLeft: 16, margin: "4px 0",
                        display: "flex", gap: 8,
                      }}>
                        <span style={{ color: currentPhase.accent }}>→</span>
                        <span>{line.replace(/^[—\-]\s*/, "")}</span>
                      </div>
                    );
                  }

                  // Numbered items
                  if (/^\d+\./.test(line.trim())) {
                    return (
                      <div key={i} style={{
                        paddingLeft: 16, margin: "4px 0",
                        display: "flex", gap: 8,
                      }}>
                        <span style={{ color: currentPhase.accent, fontWeight: 700 }}>
                          {line.trim().match(/^\d+/)[0]}.
                        </span>
                        <span>{line.replace(/^\d+\.\s*/, "")}</span>
                      </div>
                    );
                  }

                  // Warning/info
                  if (line.startsWith("⚠️") || line.startsWith("💡")) {
                    return (
                      <div key={i} style={{
                        background: line.startsWith("⚠️") ? "#7C2D1244" : "#1E3A5F44",
                        border: `1px solid ${line.startsWith("⚠️") ? "#DC262644" : "#0369A144"}`,
                        borderRadius: 8,
                        padding: "10px 14px",
                        margin: "12px 0",
                        fontSize: 12,
                      }}>
                        {line}
                      </div>
                    );
                  }

                  // Empty line
                  if (line.trim() === "") {
                    return <div key={i} style={{ height: 8 }} />;
                  }

                  return <p key={i} style={{ margin: "4px 0" }}>{line}</p>;
                })}

                {/* Render code blocks */}
                {currentStep.content.split("```").map((block, i) => {
                  if (i % 2 === 1) {
                    const lines = block.split("\n");
                    const lang = lines[0].trim();
                    const code = lines.slice(1).join("\n");
                    return (
                      <div key={`code-${i}`} style={{
                        margin: "14px 0",
                        borderRadius: 10,
                        overflow: "hidden",
                        border: "1px solid #1E293B",
                      }}>
                        <div style={{
                          padding: "6px 14px",
                          background: "#0F172A",
                          fontSize: 10,
                          color: "#64748B",
                          borderBottom: "1px solid #1E293B",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}>
                          {lang || "shell"}
                        </div>
                        <pre style={{
                          margin: 0,
                          padding: "14px",
                          background: "#080810",
                          fontSize: 11,
                          lineHeight: 1.6,
                          overflowX: "auto",
                          color: "#A5F3FC",
                        }}>
                          <code>{code}</code>
                        </pre>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Navigation */}
              <div style={{
                display: "flex", justifyContent: "space-between",
                marginTop: 28, paddingTop: 20,
                borderTop: "1px solid #1E293B",
              }}>
                <button
                  onClick={() => {
                    if (activeStep > 0) {
                      setActiveStep(activeStep - 1);
                    } else if (activePhase > 0) {
                      setActivePhase(activePhase - 1);
                      setActiveStep(phases[activePhase - 1].concepts.length - 1);
                    }
                  }}
                  disabled={activePhase === 0 && activeStep === 0}
                  style={{
                    padding: "10px 20px",
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #374151",
                    background: "transparent",
                    color: activePhase === 0 && activeStep === 0 ? "#374151" : "#94A3B8",
                    cursor: activePhase === 0 && activeStep === 0 ? "default" : "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  ← Previous
                </button>
                <button
                  onClick={() => {
                    if (activeStep < currentPhase.concepts.length - 1) {
                      setActiveStep(activeStep + 1);
                    } else if (activePhase < phases.length - 1) {
                      setActivePhase(activePhase + 1);
                      setActiveStep(0);
                    }
                  }}
                  disabled={activePhase === phases.length - 1 && activeStep === currentPhase.concepts.length - 1}
                  style={{
                    padding: "10px 20px",
                    fontSize: 12,
                    borderRadius: 8,
                    border: `1px solid ${currentPhase.accent}44`,
                    background: currentPhase.color + "33",
                    color: currentPhase.accent,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontWeight: 600,
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
